import { API } from '../config/api';

/**
 * Global Error Handler - Captures uncaught errors and unhandled promise rejections
 * Sends them to the server for monitoring
 */

let isInitialized = false;

export function initGlobalErrorHandler() {
  if (isInitialized) return;
  isInitialized = true;

  // Handle uncaught errors
  window.onerror = function(message, source, lineno, colno, error) {
    logClientError({
      message: String(message),
      stack: error?.stack || `at ${source}:${lineno}:${colno}`,
      type: 'window_onerror'
    });
    
    // Return false to allow default browser error handling in dev
    return process.env.NODE_ENV === 'production';
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = function(event) {
    const error = event.reason;
    logClientError({
      message: error?.message || String(error),
      stack: error?.stack || 'No stack trace available',
      type: 'unhandled_rejection'
    });
  };

  // Handle resource loading errors (images, scripts, etc.)
  window.addEventListener('error', function(event) {
    // Only handle resource errors, not script errors (those go to onerror)
    if (event.target && (event.target.tagName === 'IMG' || event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK')) {
      logClientError({
        message: `Failed to load resource: ${event.target.src || event.target.href}`,
        stack: `Resource type: ${event.target.tagName}`,
        type: 'resource_error'
      });
    }
  }, true);

  console.log('[GlobalErrorHandler] Initialized');
}

async function logClientError({ message, stack, type }) {
  try {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    // Don't log errors about logging errors (prevent infinite loop)
    if (message.includes('/api/client-error')) {
      console.warn('[GlobalErrorHandler] Skipping error about error logging endpoint');
      return;
    }

    await fetch(`${API}/client-error`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message: message?.substring(0, 2000),
        stack: stack?.substring(0, 10000),
        route: window.location.pathname,
        userAgent: navigator.userAgent,
        buildVersion: process.env.REACT_APP_VERSION || 'unknown',
        errorInfo: {
          type,
          url: window.location.href,
          timestamp: new Date().toISOString(),
          referrer: document.referrer
        }
      })
    });
  } catch (e) {
    // Silently fail - don't want to create more errors
    console.warn('[GlobalErrorHandler] Failed to log error:', e.message);
  }
}

/**
 * Manual error logging - use this to log caught errors
 */
export function logError(error, context = {}) {
  logClientError({
    message: error?.message || String(error),
    stack: error?.stack,
    type: context.type || 'manual_log',
    ...context
  });
}

export default initGlobalErrorHandler;
